import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Loader2 } from 'lucide-react'
import { DATA_SOURCES } from './types'

interface EntitySelectorProps {
  dataSource: string
  fieldCount: number
  fieldsLoading: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onDataSourceChange: (dataSource: string) => void
}

export function EntitySelector({
  dataSource,
  fieldCount,
  fieldsLoading,
  open,
  onOpenChange,
  onDataSourceChange,
}: EntitySelectorProps) {
  return (
    <Card>
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="flex flex-row items-center justify-between p-4 cursor-pointer hover:bg-muted/50 rounded-t-lg">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Data Source</CardTitle>
              {dataSource && (
                <Badge variant="secondary" className="text-xs capitalize">
                  {dataSource}
                </Badge>
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            <div>
              <Label htmlFor="data-source">Select Data Source *</Label>
              <Select value={dataSource} onValueChange={onDataSourceChange}>
                <SelectTrigger id="data-source" aria-required="true">
                  <SelectValue placeholder="Choose a data source" />
                </SelectTrigger>
                <SelectContent>
                  {DATA_SOURCES.map((source) => (
                    <SelectItem key={source.value} value={source.value}>
                      <span>{source.label}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {source.description}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {dataSource &&
              (() => {
                const currentSource = DATA_SOURCES.find((s) => s.value === dataSource)
                return (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    {currentSource?.description && (
                      <p className="text-sm text-muted-foreground mb-1">
                        {currentSource.description}
                      </p>
                    )}
                    {fieldsLoading ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        <span className="text-sm text-muted-foreground">Loading fields...</span>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">{fieldCount} fields available</p>
                    )}
                  </div>
                )
              })()}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
